import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  CourseFormat,
  CourseFormState,
  CourseStatus,
  MaterialType,
} from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { MaterialsForm } from '@/components/resources/forms';
import { useSaveCourse } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  generateCourseTranslationsInitialValues,
  materialByTypeCreatePath,
  materialByTypeListPath,
  parseModifiedCourseFiles,
  parseModifiedCourseTranslations,
  queryStateConverter,
  updateMaterialByTypePath,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { MenuItem } from 'primereact/menuitem';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const getBreadcrumbs = (
  type: MaterialType,
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  const isResource = type === MaterialType.RESOURCE;
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: isResource ? t('templates') : t('posters'),
      url: materialByTypeListPath(type),
      className: 'active',
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('generic.createNew'),
      url: materialByTypeCreatePath(type),
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const MaterialCreatePage: React.FC<{ type: MaterialType }> = ({
  type,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);

  const [formInitialValues] = useState<CourseFormState>({
    courseTranslations: generateCourseTranslationsInitialValues(),
    courseLanguages: account?.isSystem ? [LanguagesEnum.EN] : [],
    courseThumbnail: '',
    courseFiles: account?.isSystem
      ? [
          {
            translations: {
              [LanguagesEnum.EN]: {
                file: null,
              },
            },
          },
        ]
      : [],
    format: CourseFormat.FILES,
    courseContents: [],
    courseCreatedAt: undefined,
    courseLastUpdatedAt: undefined,
    type: type ?? MaterialType.RESOURCE,
    isStandardCatalogue: false,
    releaseDate: null,
    customContent: false,
    customAccount: null,
    selfHosted: true,
    hubspotProperty: undefined,
    hubspotDateProperty: undefined,
    categories: undefined,
    exemptFromMinimumRequirementScore: false,
    plannerScheduleDate: null,
  });

  const createCourse = useSaveCourse();

  const handleSubmit = async (data: FormikValues) => {
    try {
      if (!account) return;

      const response = await createCourse.create({
        courseTranslations: parseModifiedCourseTranslations(
          data.courseTranslations,
        ),
        courseLanguages: data.courseLanguages,
        format: CourseFormat.FILES,
        courseThumbnail: data.courseThumbnail ? data.courseThumbnail : null,
        courseFiles: parseModifiedCourseFiles(data.courseFiles, {
          selfHosted: data.selfHosted,
          courseLanguages: data.courseLanguages,
        }),
        courseCreatedAt: data.courseCreatedAt,
        courseLastUpdatedAt: data.courseLastUpdatedAt,
        type: data.type,
        isStandardCatalogue: data.isStandardCatalogue,
        releaseDate: data.releaseDate,
        plannerScheduleDate: data.plannerScheduleDate,
        account:
          data.customContent && !!data.customAccount
            ? data.customAccount
            : null,
        hubspotProperty: data.hubspotProperty,
        hubspotDateProperty: data.hubspotDateProperty,
        categories: data.categories,
        status: CourseStatus.SAVED,
      });

      toast?.success(t('toast.success'), t('material.created'));

      navigate(updateMaterialByTypePath(type, response.id));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <>
      <AppBreadCrumb model={getBreadcrumbs(type, account as Account, t)} />
      <FlexContainer justify="flex-start">
        <MaterialsForm
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          state={queryStateConverter(createCourse)}
          isCreate
        />
      </FlexContainer>
    </>
  );
};
